import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const ManagePolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', policy_type: 'general', is_published: true });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.getAllPolicies();
      setPolicies(res.data);
    } catch { toast.error('Failed to load policies'); }
  };

  const resetForm = () => {
    setForm({ title: '', content: '', policy_type: 'general', is_published: true });
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    try {
      if (editing) {
        await api.updatePolicy(editing, form);
        toast.success('Policy updated');
      } else {
        await api.createPolicy(form);
        toast.success('Policy created');
      }
      resetForm();
      load();
    } catch { toast.error('Failed to save policy'); }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({ title: p.title, content: p.content, policy_type: p.policy_type || 'general', is_published: p.is_published });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await api.deletePolicy(id);
      toast.success('Policy deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (p) => {
    try {
      await api.updatePolicy(p.id, { is_published: !p.is_published });
      load();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="manage-policies">
      <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Manage Policies</h2>

      {/* Form */}
      <div className="border border-[#E6DCD3] rounded-xl p-6 mb-8 bg-[#FCFBF9]" data-testid="policy-form">
        <h3 className="text-sm font-medium text-[#1A1A1A] mb-4">{editing ? 'Edit Policy' : 'Add Policy'}</h3>
        <input
          value={form.title}
          onChange={e => setForm({...form, title: e.target.value})}
          placeholder="Policy title"
          className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961] mb-4"
          data-testid="policy-title-input"
        />
        <textarea
          value={form.content}
          onChange={e => setForm({...form, content: e.target.value})}
          placeholder="Policy content..."
          rows={6}
          className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961] mb-4"
          data-testid="policy-content-input"
        />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} className="rounded" />
            Published
          </label>
          <div className="flex-1" />
          {editing && <button onClick={resetForm} className="text-sm text-[#786E66] hover:text-[#1A1A1A]">Cancel</button>}
          <button onClick={handleSave} className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-6 py-2 text-sm transition-all inline-flex items-center gap-2" data-testid="policy-save-btn">
            <Plus className="h-4 w-4" /> {editing ? 'Update' : 'Add'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="policies-list">
        {policies.map((p, idx) => (
          <div key={p.id} className={`border rounded-xl p-5 ${p.is_published ? 'border-[#E6DCD3] bg-white' : 'border-gray-200 bg-gray-50 opacity-70'}`} data-testid={`policy-item-${idx}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1A1A1A] text-sm mb-1">{p.title}</p>
                <p className="text-sm text-[#786E66] line-clamp-3 whitespace-pre-wrap">{p.content}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => togglePublish(p)} className="p-2 hover:bg-[#F3EBE1] rounded-lg" title={p.is_published ? 'Unpublish' : 'Publish'}>
                  {p.is_published ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                </button>
                <button onClick={() => handleEdit(p)} className="p-2 hover:bg-[#F3EBE1] rounded-lg"><Pencil className="h-4 w-4 text-[#3E2723]" /></button>
                <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-500" /></button>
              </div>
            </div>
          </div>
        ))}
        {policies.length === 0 && <p className="text-center text-[#786E66] text-sm py-8">No policies yet. Add one above.</p>}
      </div>
    </div>
  );
};

export default ManagePolicies;
